import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { useCMSSide } from '../hooks/useCMS'

export function HypnoterapiPage() {
  const { side: cmsSide } = useCMSSide('hypnoterapi')

  useSEO({
    title: cmsSide?.seo_titel || 'HYPNOTERAPI | BoSteendahl.dk',
    description: cmsSide?.seo_beskrivelse || 'Hypnoterapi handler om tillid, tryghed og samarbejde. 95 % af vores adfærd er styret af underbevidstheden. Skab vedvarende forandring hos Bo Steendahl.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/logo-11-dec.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Kerneområde · Certified Practitioner
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Hypnoterapi'}
          </h1>
          <p className="mt-4 text-xl sm:text-2xl font-serif text-[#1b3a4b]/90 max-w-3xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Hypnoterapi handler om tillid, tryghed og samarbejde.'}
          </p>
        </div>
      </section>

      {/* Intro Quote Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="bg-[#172e3b] text-white rounded-2xl p-8 sm:p-10 shadow-xl border border-white/10">
          <div className="flex items-center gap-3 text-[#7c875a] font-semibold text-xs uppercase tracking-wider mb-2">
            <i className="fa-solid fa-brain" aria-hidden="true" />
            <span>Underbevidsthedens kraft</span>
          </div>
          <h2 className="font-serif text-[21px] sm:text-3xl font-bold text-white mb-3">
            Den positive intension
          </h2>
          <p className="text-[#FAF8F5]/90 text-sm sm:text-base leading-relaxed">
            Ifølge mange forskere er <strong>95 % af vores adfærd styret af underbevidstheden</strong>.
          </p>
          <p className="mt-3 text-sm text-[#FAF8F5]/80 leading-relaxed">
            Underbevidstheden har intet ønske om at skabe forandring eller udvikling, den har mere lyst til at bevare alting trygt og velkendt som det den kender. Underbevidstheden kører på autopilot og dens primære opgave er at passe på dig og den du er i din kerne. Det betyder at der bag enhver vane, tankemønster, overbevisning og adfærd ligger en indbygget positiv intension, om at beskytte dig.
          </p>
        </div>
      </section>

      {/* Main Verbatim Scrape Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-12">
          {/* Positiv intention uddybet */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Hvorfor adfærdsændringer kan være vanskelige
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Det er ikke altid let at gennemskue hvordan dårlige vaner som f.eks. rygning eller overspisning kan have en positiv intension. Men rygning er formentlig ikke startet for bevidst at skade sig selv, og øge risikoen for forhøjet blodtryk, lungecancer og kortere levetid, men måske i stedet ud fra et ønske om accept, fællesskab og samhørighed med andre. Overspisning kan være et ubevidst forsøg på at skabe mindre opmærksomhed om sin egen person, ved at gøre sig selv mindre attraktiv.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Uanset hvilken positive intensioner som ligger bag den uhensigtsmæssige adfærd, er det afgørende at anerkender adfæden som en vanen der kører automatisk på et ubevidst plan.
            </p>
            <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
              Årsagen til at adfærdsændringer er så vanskelige at gennemføre og fastholde, er at den positive intension som ligger skjult i underbevidstheden, ikke imødekommes ved indførelse af den nye adfærd. Først når underbevidstheden føler sig tryg og har tillid til at den positive intension imødekommes på anden vis, vil den nye adfærd overskrive den gamle vane i det underbevidste sind.
            </blockquote>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Tryghed er en forudsætning for at skabe vedvarende forandring i underbevidstheden. Ubevidste vaner, mønstre, overbevisninger og adfærd opstår over tid. Med en fokuseret og stærk viljestyrke kan ny adfærd og nye vaner skabes, men hvis det ubevidste sind ikke har tillid til at du i din kerne er sikret, vil den gamle adfærd blive ved med at trække i dig, for at få dig tilbage i sikkerhed.
            </p>
          </div>

          {/* Hvad sker der i sindet under hypnosen */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
              Tryghed og samarbejde
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
              Hvad sker der i sindet under hypnosen ?
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Under hypnosen er du i en naturlig bevidsthedstilstand hvor paraderne er sænket og barriererne lagt ned. Adgangen til det underbevidste sind er åbent for påvirkning og forandring af negative tanker og begrænsende overbevisninger, dårlige vaner og uhensigtsmæssige adfærdsmønstre.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              <strong>Det er vigtigt at understrege, at du altid er i kontrol under en hypnoterapi-session</strong>, og du ikke kan påvirkes eller tvinges til at gøre noget imod din vilje. Hypnoterapi handler om samarbejde mellem dig som klient og mig som terapeut.
            </p>
          </div>

          {/* Hvad er Hypnoterapi */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Hvad er Hypnoterapi?
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Hypnoterapi er en behandlingsform, hvor hypnose bruges til at guide dig ned i en dyb fokuseret mental tilstand, hvor du på samme måde som når du er helt opslugt af en god bog, eller fuld fokuseret på handlingen i en spændende film, glemmer alt om tid og sted, og kun har 100 procent fokus på den ene ting.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Du kender også denne tilstand fra når du falder i staver ved morgenkaffen, eller når du kommer hjem uden helt at kunne huske, hvordan du egentlig kom frem. Du er vågen og din opmærksomhed er dybt opslugt og fokuseret, men du er ikke helt til stede, som du er når du taler med et andet menneske.
            </p>
          </div>

          {/* Virker det? Forskning */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
            <div className="border-b border-[#E5E0D8] pb-4">
              <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
                Videnskabelig dokumentation
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                Virker det?
              </h3>
              <p className="mt-2 text-base text-[#475569] leading-relaxed">
                Det korte svar er <strong>Ja, Hypnoterapi virker</strong>. Der er gennem tiden gennemført mange studier som dokumenterer hypnoterapiens effektivitet. Nedenfor har jeg samlet et lille udsnit med links til forskningsprojekter som dokumentere effekten, men der findes mange andre.
              </p>
              <p className="mt-2 text-sm text-[#64748B]">
                Hypnoterapi kan bruges til en lang række problemstillinger som angst, stress, jalousi, fobier, OCD/tvangstanker, manglende selvværd og selvtillid, rygestop, vægttab, præstationsangst, sukkerafhængighed, spiseforstyrrelser, traumebehandling og meget andet.
              </p>
            </div>

            <div className="space-y-6 text-sm text-[#475569]">
              {/* #1 */}
              <div className="p-5 rounded-xl bg-[#FAF8F5] border border-[#E5E0D8]">
                <h4 className="font-serif font-bold text-base text-[#1b3a4b] mb-1">
                  #1 Svigtende hukommelse
                </h4>
                <p className="text-xs text-[#64748B] mb-2">
                  Dansk forskningsprojekt som dokumenterer forbedret arbejdshukommelsen efter behandling med Hypnoterapi hos personer med senhjerneskade.
                </p>
                <a
                  href="https://academic.oup.com/brain/article/140/4/1100/2970094?login=false"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-[#1b3a4b] hover:text-[#7c875a] underline flex items-center gap-1.5"
                >
                  <span>Læs forskningsartiklen på Oxford Academic (Brain)</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </a>
              </div>

              {/* #2 */}
              <div className="p-5 rounded-xl bg-[#FAF8F5] border border-[#E5E0D8] space-y-4">
                <h4 className="font-serif font-bold text-base text-[#1b3a4b]">
                  #2 Smerter
                </h4>
                <div className="space-y-3 pl-3 border-l-2 border-[#7c875a]/50">
                  <div>
                    <strong className="text-xs text-[#1b3a4b] block">#2.1 Smertelindring</strong>
                    <p className="text-xs text-[#64748B]">
                      Gennemgang af 18 studier som viser en moderat til stor effekt baseret på analyser for mere end 900 deltagere.
                    </p>
                    <a
                      href="https://pubmed.ncbi.nlm.nih.gov/10769981/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#1b3a4b] hover:text-[#7c875a] underline"
                    >
                      PubMed studie (10769981)
                    </a>
                  </div>
                  <div>
                    <strong className="text-xs text-[#1b3a4b] block">#2.2 Kroniske smerter</strong>
                    <p className="text-xs text-[#64748B]">
                      Sammenligning af fem behandlingsmetoder. Hypnoterapi har tendens til større smertereduktion sammenlignet med behandlingsformer uden hypnose.
                    </p>
                    <a
                      href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2753288/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#1b3a4b] hover:text-[#7c875a] underline"
                    >
                      NCBI PMC studie (PMC2753288)
                    </a>
                  </div>
                  <div>
                    <strong className="text-xs text-[#1b3a4b] block">#2.3 Akutte smerter</strong>
                    <p className="text-xs text-[#64748B]">
                      Hypnose i forbindelse med operation. Klinisk evidens fra 29 randomiserede kontrollerede kliniske forsøg.
                    </p>
                    <a
                      href="https://pubmed.ncbi.nlm.nih.gov/26599994/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#1b3a4b] hover:text-[#7c875a] underline"
                    >
                      PubMed studie (26599994)
                    </a>
                  </div>
                  <div>
                    <strong className="text-xs text-[#1b3a4b] block">#2.4 Fantom smerter</strong>
                    <p className="text-xs text-[#64748B]">
                      Casestudie af hypnoterapi for behandling af smerter i amputerede lemmer.
                    </p>
                  </div>
                </div>
              </div>

              {/* #3 */}
              <div className="p-5 rounded-xl bg-[#FAF8F5] border border-[#E5E0D8]">
                <h4 className="font-serif font-bold text-base text-[#1b3a4b] mb-1">
                  #3 Depression
                </h4>
                <p className="text-xs text-[#64748B] mb-2">
                  Evaluering af 10 undersøgelser, der omfattede 13 forsøg med hypnose. Den gennemsnitlige deltager, der modtog hypnose, viste mere forbedring end omkring 76 % af kontroldeltagerne.
                </p>
                <a
                  href="https://pubmed.ncbi.nlm.nih.gov/34874235/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-[#1b3a4b] hover:text-[#7c875a] underline flex items-center gap-1.5"
                >
                  <span>PubMed studie (34874235)</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
